import { useId, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

const SIZE = 82
const VIEWBOX = '0 0 82 82'
const WAVE_AMPLITUDE = 2.8
const WAVE_SPEED = 0.035
const WAVE_STEPS = 32

function buildWavePath(
  clipY: number,
  phase: number,
  amplitude: number,
): string {
  const parts: string[] = []
  for (let i = 0; i <= WAVE_STEPS; i++) {
    const t = i / WAVE_STEPS
    const x = t * SIZE
    const primary = Math.sin(t * Math.PI * 2 + phase) * amplitude
    const secondary = Math.sin(t * Math.PI * 3.5 + phase * 0.6) * amplitude * 0.35
    const y = clipY + primary + secondary
    parts.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`)
  }
  parts.push(`L${SIZE} ${SIZE} L0 ${SIZE} Z`)
  return parts.join(' ')
}

export interface SegmentProgressIconProps {
  /** 0–100. Gradient fill reveals from bottom to top. */
  percent: number
  className?: string
  /** Size in px (default 82). */
  size?: number
}

/**
 * Custom L-shaped progress icon from Figma (node 619-4011).
 * Dark base paths plus gradient-filled paths clipped by progress (0–100%).
 * Clip shape uses an animated sine-wave edge for a water-fill look.
 */
export function SegmentProgressIcon({
  percent,
  className,
  size = SIZE,
}: SegmentProgressIconProps) {
  const id = useId().replace(/:/g, '')
  const clamped = Math.max(0, Math.min(100, percent))
  const clipY = SIZE - (clamped / 100) * SIZE

  const waveRef = useRef<SVGPathElement>(null)
  const phaseRef = useRef(0)
  const rafRef = useRef<number>(0)

  const edgeFade = Math.min(clamped / 12, (100 - clamped) / 12, 1)
  const amp = WAVE_AMPLITUDE * Math.max(edgeFade, 0)

  useEffect(() => {
    const el = waveRef.current
    if (!el) return

    if (clamped <= 0 || clamped >= 100) {
      el.setAttribute(
        'd',
        `M0 ${clipY} L${SIZE} ${clipY} L${SIZE} ${SIZE} L0 ${SIZE} Z`,
      )
      return
    }

    const tick = () => {
      phaseRef.current += WAVE_SPEED
      el.setAttribute('d', buildWavePath(clipY, phaseRef.current, amp))
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [clipY, amp, clamped])

  return (
    <svg
      width={size}
      height={size}
      viewBox={VIEWBOX}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('overflow-visible', className)}
      aria-hidden
    >
      <defs>
        <clipPath id={`segment-progress-clip-${id}`}>
          <path
            ref={waveRef}
            d={buildWavePath(clipY, 0, amp)}
          />
        </clipPath>
        <linearGradient
          id={`paint0_linear_${id}`}
          x1="41"
          y1="0"
          x2="41"
          y2="82"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="var(--segment-progress-top)" />
          <stop offset="1" stopColor="var(--segment-progress-bottom)" />
        </linearGradient>
        <linearGradient
          id={`paint1_linear_${id}`}
          x1="41"
          y1="0"
          x2="41"
          y2="82"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="var(--segment-progress-top)" />
          <stop offset="1" stopColor="var(--segment-progress-bottom)" />
        </linearGradient>
      </defs>
      {/* Base (unfilled) – dark paths */}
      <g opacity="0.6">
        <path
          d="M49.3939 46.5987C47.8511 46.5985 46.6009 45.3475 46.6009 43.8046V38.1943C46.6009 36.6514 45.3498 35.4004 43.8069 35.4002H38.1954C36.6524 35.4002 35.4013 34.1492 35.4013 32.6061L35.4013 2.79408C35.4013 1.25104 36.6524 -3.37242e-08 38.1954 0L43.8058 4.90471e-07C45.3487 9.18226e-05 46.5998 1.2511 46.5998 2.79409L46.5998 27.4988L55.5515 18.5471C56.6425 17.4562 58.4112 17.4564 59.5023 18.5471L63.4696 22.5144C64.5607 23.6055 64.5606 25.374 63.4696 26.4651L54.5356 35.4002L79.206 35.4002C80.7489 35.4002 81.9999 36.6514 82.0001 38.1943V43.8046C82.0001 45.3477 80.749 46.5987 79.206 46.5987L49.3939 46.5987Z"
          fill="#3E6161"
        />
        <path
          d="M38.1943 82.0012C36.6513 82.0011 35.4002 80.7501 35.4002 79.2071V54.4736L26.4197 63.4541C25.3286 64.5452 23.559 64.5452 22.4679 63.4541L18.5006 59.4867C17.4099 58.3958 17.4101 56.6271 18.5006 55.536L27.4356 46.6009L2.79408 46.6009C1.25114 46.6009 0.000149659 45.3498 0 43.8069L4.90471e-07 38.1965C6.59092e-07 36.6535 1.25104 35.4024 2.79409 35.4024H32.6061C34.149 35.4027 35.3991 36.6537 35.3991 38.1965V43.8069C35.3991 45.3498 36.6503 46.6008 38.1932 46.6009H43.8046C45.3477 46.6009 46.5987 47.852 46.5987 49.395L46.5987 79.2071C46.5987 80.7501 45.3477 82.0012 43.8046 82.0012H38.1943Z"
          fill="#3E6161"
        />
      </g>
      {/* Filled (gradient) – clipped by animated wave, fills like water */}
      <g clipPath={`url(#segment-progress-clip-${id})`}>
        <path
          d="M49.3939 46.5987C47.8511 46.5985 46.6009 45.3475 46.6009 43.8046V38.1943C46.6009 36.6514 45.3498 35.4004 43.8069 35.4002H38.1954C36.6524 35.4002 35.4013 34.1492 35.4013 32.6061L35.4013 2.79408C35.4013 1.25104 36.6524 -3.37242e-08 38.1954 0L43.8058 4.90471e-07C45.3487 9.18226e-05 46.5998 1.2511 46.5998 2.79409L46.5998 27.4988L55.5515 18.5471C56.6425 17.4562 58.4112 17.4564 59.5023 18.5471L63.4696 22.5144C64.5607 23.6055 64.5606 25.374 63.4696 26.4651L54.5356 35.4002L79.206 35.4002C80.7489 35.4002 81.9999 36.6514 82.0001 38.1943V43.8046C82.0001 45.3477 80.749 46.5987 79.206 46.5987L49.3939 46.5987Z"
          fill={`url(#paint0_linear_${id})`}
        />
        <path
          d="M38.1943 82.0012C36.6513 82.0011 35.4002 80.7501 35.4002 79.2071V54.4736L26.4197 63.4541C25.3286 64.5452 23.559 64.5452 22.4679 63.4541L18.5006 59.4867C17.4099 58.3958 17.4101 56.6271 18.5006 55.536L27.4356 46.6009L2.79408 46.6009C1.25114 46.6009 0.000149659 45.3498 0 43.8069L4.90471e-07 38.1965C6.59092e-07 36.6535 1.25104 35.4024 2.79409 35.4024H32.6061C34.149 35.4027 35.3991 36.6537 35.3991 38.1965V43.8069C35.3991 45.3498 36.6503 46.6008 38.1932 46.6009H43.8046C45.3477 46.6009 46.5987 47.852 46.5987 49.395L46.5987 79.2071C46.5987 80.7501 45.3477 82.0012 43.8046 82.0012H38.1943Z"
          fill={`url(#paint1_linear_${id})`}
        />
      </g>
    </svg>
  )
}
